"use client";

import { Typography, Box, Container, IconButton, Stack } from "@mui/material";
import { LocationOn, Instagram, Facebook } from "@mui/icons-material";

// Non-discrimination disclaimer text
const DISCLAIMER_TEXT = `Sun Hive Microschool is open to all students who meet our entrance requirements, including past academic performance and discipline record and does not discriminate based on race, color, religion, gender, gender identity or expression, sexual orientation, national origin, age, disability, marital status, veteran status, or any other characteristic protected by law in the administration of our educational policies, admissions policies, scholarship and loan programs, and athletic or other school-administered programs. Sun Hive Microschool reserves the right to deny admission to students with a history of poor academic performance and/or disciplinary problems.`;

export default function SchoolFooter() {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: "primary.main",
        color: "text.inverse",
        py: { xs: 3, md: 2 },
      }}
    >
      <Container maxWidth="xl">
        {/* Top Row: Identity (Left) & Contact/Socials (Right) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "center", md: "flex-end" }, // Align to bottom for solid baseline
            gap: 4,
            mb: 4,
          }}
        >
          {/* Left: School Name & Tagline */}
          <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
            <Typography
              component="p"
              sx={{
                fontWeight: 700,
                fontSize: "1.5rem",
                color: "inherit",
                mb: 0.5,
              }}
            >
              Sun Hive Microschool
            </Typography>
            <Typography
              sx={{
                fontSize: "1rem",
                opacity: 0.9,
                color: "inherit",
                letterSpacing: "0.02em",
              }}
            >
              Empowering students to enrich their future.
            </Typography>
          </Box>

          {/* Right: Location & Socials grouped */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              gap: { xs: 3, md: 4 },
            }}
          >
            {/* Location */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                opacity: 0.9,
              }}
            >
              <LocationOn fontSize="small" />
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
                El Paso, TX
              </Typography>
            </Box>

            {/* Social Icons */}
            <Stack direction="row" spacing={1}>
              <IconButton
                href="https://www.facebook.com/profile.php?id=61559017865669"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                sx={{
                  color: "inherit",
                  border: "1px solid rgba(255,255,255,0.2)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                }}
              >
                <Facebook />
              </IconButton>
              <IconButton
                href="https://www.instagram.com/sunhive_microschool/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                sx={{
                  color: "inherit",
                  border: "1px solid rgba(255,255,255,0.2)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                }}
              >
                <Instagram />
              </IconButton>
            </Stack>
          </Box>
        </Box>

        {/* Divider */}
        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "rgba(255,255,255,0.15)",
            mb: 4,
          }}
        />

        {/* Bottom Section: Disclaimer + Copyright - Centered for balance */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          {/* Non-Discrimination Disclaimer */}
          <Typography
            sx={{
              fontSize: "0.75rem",
              lineHeight: 1.6,
              opacity: 0.75,
              color: "inherit",
              textAlign: "center",
              // mx: "auto" - Removed to allow full width per user request
            }}
          >
            <strong>Non-Discrimination Policy:</strong> {DISCLAIMER_TEXT}
          </Typography>

          {/* Copyright */}
          <Typography
            sx={{
              fontSize: "0.85rem",
              opacity: 0.85,
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            &copy; 2024 - {currentYear} Sun Hive Microschool. All rights
            reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
